// Generated Zod schemas from Frappe doctype
import { z } from "zod";
import * as ENUMS from "@/models/enums";

export const personalInformationSchema = z.object({
  first_name: z.string().min(1, "First name is required"),
  middle_name: z.string().optional(),
  last_name: z.string().min(1, "Last name is required"),
  gender: ENUMS.GENDER_ENUM,
  date_of_birth: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Invalid date format (YYYY-MM-DD)").refine((dateString) => {
    // Check if the person is at least 18 years old
    const birthDate = new Date(dateString);
    const today = new Date();

    // Calculate age by getting the difference in years
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();

    // Adjust age if birthday hasn't occurred yet this year
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }

    return age >= 18;
  }, "You must be at least 18 years old"),

  passport_photo: z.instanceof(FileList).refine((value) => value.length > 0, "Passport photo is required")
});
export type PersonalInformation = z.infer<typeof personalInformationSchema>;

export const meansOfIdentificationSchema = z.object({
  id_type: ENUMS.ID_TYPE_ENUM,
  id_number: z.string().min(1, "ID number is required"),
  id_photo: z.instanceof(FileList).refine((value) => value.length > 0, "ID photo is required"),
  bvn: z.string().optional(),
  bvn_photo: z.instanceof(FileList).optional()
});
export type MeansOfIdentification = z.infer<typeof meansOfIdentificationSchema>;

export const contactInformationSchema = z.object({
  phone_number: z.string()
    .min(10, "Phone number must be at least 10 digits")
    .max(15, "Phone number must be at most 15 digits")
    .transform(phone => phone.replace(/[^0-9]/g, ''))
    .pipe(
      z.string().refine(
        phone => {
          const phoneRegex = /^[0-9]{10,15}$/;
          return phoneRegex.test(phone);
        },
        "Invalid phone number format"
      )
    )
});
export type ContactInformation = z.infer<typeof contactInformationSchema>;

export const residentialInformationSchema = z.object({
  state: ENUMS.STATE_ENUM
});
export type ResidentialInformation = z.infer<typeof residentialInformationSchema>;

export const clusterInformationSchema = z.object({
  cluster_name: ENUMS.CLUSTER_ENUM,
  cooperative_name: z.string().min(1, "Cooperative name is required"),
  cooperative_role: ENUMS.COOPERATIVE_ROLE_ENUM
});
export type ClusterInformation = z.infer<typeof clusterInformationSchema>;

export const farmInformationSchema = z.object({
  commodity: ENUMS.COMMODITY_ENUM,
  size_in_hectares: z.number(),
  previously_used_for: ENUMS.PREVIOUSLY_USED_FOR_ENUM,
  longitude: z.number(),
  latitude: z.number(),
  number_of_hectares_financed: z.number(),
  last_year_harvest_yield: ENUMS.COMMODITY_ENUM,
  number_of_bags_harvested: z.number(),
  cost_per_bag: z.number()
});
export type FarmInformation = z.infer<typeof farmInformationSchema>;

export const packageInformationSchema = z.object({
  input_package: ENUMS.INPUT_PACKAGE_ENUM,
  number_of_hectares_subscribed: z.number(),
  signature: z.instanceof(FileList).refine((value) => value.length > 0, "Signature is required"),
});
export type PackageInformation = z.infer<typeof packageInformationSchema>;

